import type Rollbar from 'rollbar/replay'

import { isChunkLoadErrorMessage, isPageHidden, isPageUnloading } from '@/plugins/chunkLoadRecovery'

interface RollbarTrace {
  exception?: { message?: unknown }
}

/** The parts of the item `checkIgnore` receives (Rollbar's `item.data`) that the filter reads. */
interface RollbarItemData {
  person?: { id?: unknown }
  body?: {
    message?: { body?: unknown }
    trace?: RollbarTrace
    trace_chain?: RollbarTrace[]
  }
}

/** Every error message an item carries: an uncaught error arrives as `trace` or `trace_chain`
 * (a cause chain), a logged string as `message`. */
export function itemMessages(payload: unknown): string[] {
  const body = (payload as RollbarItemData | undefined)?.body
  const traces = [...(body?.trace_chain ?? []), ...(body?.trace ? [body.trace] : [])]

  return [body?.message?.body, ...traces.map(trace => trace.exception?.message)]
    .filter((message): message is string => typeof message === 'string')
}

export interface PageState {
  hidden: boolean
  unloading: boolean
}

/**
 * A chunk that failed to load is noise when nobody was looking at the page: an anonymous visitor
 * (email link scanners open every `/go/` link logged out), a hidden tab, or a page already going
 * away. A logged-in user on a visible tab still reports, since that is a real user stuck on a
 * broken load.
 */
export function isChunkLoadNoise(payload: unknown, page: PageState): boolean {
  const data = payload as RollbarItemData | undefined

  return itemMessages(data).some(isChunkLoadErrorMessage)
    && (!data?.person?.id || page.hidden || page.unloading)
}

/**
 * Rollbar configuration for the browser bundle, kept in its own dependency-free module so the
 * scrubbing options can be asserted by a unit test: importing `@/plugins/rollbar` pulls in the Vue
 * app, its stores and the virtual `~pages` route module, none of which load under Vitest.
 */
export const rollbarConfig: Rollbar.Configuration = {
  accessToken: import.meta.env.VITE_ROLLBAR_KEY,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: true,
  scrubFields: [
    'first_name',
    'last_name',
    'name',
    'email',
    'phone',
    'vector_email',
    'vector_phone',
    'ssn',
    'ssn_last4',
    'patient_ssn',
    'dob',
    'date_of_birth',
    'age',
    'line1',
    'line2',
    'city',
    'state',
    'zip',
    'address',
    'estimated_household_income',
    'marital_status',
    'patient_sex',
    'gender',
    'sex',

    // Rollbar matches the whole key, so IDV's prefixed patient / subscriber fields need listing individually
    'patient_uuid',
    'patient_identifier',
    'patient_first_name',
    'patient_last_name',
    'provider_patient_id',
    'mrn',
    'atb_visit_id',
    'member_id',
    'policy_number',
    'group_number',
    'subscriber_name',
    'subscriber_first',
    'subscriber_last',
    'subscriber_dob',
    'subscriber_gender',
    'patient_relationship',
    'password',
    'token',
    'access_token',
    'authorization',
  ],
  scrubHeaders: ['authorization', 'cookie'],

  // Rollbar records the value of every DOM input as a `rollbar-input-event`, and `scrubFields` only
  // masks an input whose element description matches one of the listed names (the input's own
  // `name` attribute). A field named `payer_id`, a field whose name is not listed, and a field with
  // no `name` at all each ship their typed value verbatim -- patient names, dates of birth, payer
  // and member IDs and the free-text chat question were all recorded that way. Mask the value of
  // every input rather than enumerate the field names we happen to know.
  scrubTelemetryInputs: true,

  payload: {
    environment: import.meta.env.VITE_NODE_ENV || 'development',
    client: {
      javascript: {
        source_map_enabled: true,
        code_version: import.meta.env.VITE_APP_VERSION || 'dev',
      },
    },
  },
  replay: {
    enabled: true,

    // Session replay is a second path for the same typed values: rrweb records input values and
    // masks only `password` inputs by default, and the telemetry option above does not reach the
    // recording. `maskAllInputs` is read at runtime and forwarded to rrweb verbatim (see the SDK's
    // `browser/replay/recorder`), but it is missing from the declared `ReplayOptions` type, hence
    // the cast.
    maskAllInputs: true,
  } as Rollbar.ReplayOptions,

  checkIgnore: (_isUncaught, _args, payload) => isChunkLoadNoise(payload, { hidden: isPageHidden(), unloading: isPageUnloading() }),
}
